import React from 'react';
import { useTranslation } from 'react-i18next';
import { SessionVerifyStep } from './SessionVerifyStep';
import { SocialSignInButtons } from './SocialSignInButtons';

type SessionGateProps = {
  sessionLoaded: boolean;
  session: { userId: string; email: string; token: string } | null;
  sessionStage: 'email' | 'verify';
  sessionEmailInput: string;
  setSessionEmailInput: (value: string) => void;
  sessionCodeInput: string;
  setSessionCodeInput: (value: string) => void;
  sessionConsentChecked: boolean;
  setSessionConsentChecked: (value: boolean) => void;
  sessionErrorMessage: string | null;
  setSessionErrorMessage: (value: string | null) => void;
  sessionHintCode: string | null;
  sessionResendSeconds: number;
  isSessionRequestingCode: boolean;
  isSessionSubmitting: boolean;
  setSessionStage: (stage: 'email' | 'verify') => void;
  requestSessionCode: (email: string) => void;
  verifySessionCode: (email: string, code: string) => void;
  signInWithSocial?: (provider: 'google' | 'apple', idToken: string) => void | Promise<void>;
};

export const SessionGate: React.FC<SessionGateProps> = ({
  sessionLoaded,
  session,
  sessionStage,
  sessionEmailInput,
  setSessionEmailInput,
  sessionCodeInput,
  setSessionCodeInput,
  sessionConsentChecked,
  setSessionConsentChecked,
  sessionErrorMessage,
  setSessionErrorMessage,
  sessionHintCode,
  sessionResendSeconds,
  isSessionRequestingCode,
  isSessionSubmitting,
  setSessionStage,
  requestSessionCode,
  verifySessionCode,
  signInWithSocial
}) => {
  const { t } = useTranslation();
  const isVerifyStage = sessionStage === 'verify';

  const [isNarrowSession, setIsNarrowSession] = React.useState(() => (
    typeof window !== 'undefined' ? window.matchMedia('(max-width: 640px)').matches : false
  ));

  React.useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const mq = window.matchMedia('(max-width: 640px)');
    const onChange = () => setIsNarrowSession(mq.matches);
    onChange();
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  const handleGoogleToken = React.useCallback((idToken: string) => {
    return signInWithSocial?.('google', idToken);
  }, [signInWithSocial]);

  const handleAppleToken = React.useCallback((idToken: string) => {
    return signInWithSocial?.('apple', idToken);
  }, [signInWithSocial]);

  const hasSocialProviders = Boolean(
    (import.meta as any).env?.VITE_GOOGLE_CLIENT_ID ||
    (import.meta as any).env?.VITE_APPLE_CLIENT_ID
  );

  if (!sessionLoaded) {
    return (
      <div className="sessionGate">
        <div className="sessionShell sessionShellLoading">
          <div className="sessionCard sessionCardPrimary sessionCard--liquid">
            <div className="brandWordmark brandWordmark--centered">
              <img src="/noox-wordmark-auth.png" alt="NOOX" className="brandLogo brandLogo--session" width={200} height={44} />
            </div>
            <div className="sub">{t('common.loading')}</div>
          </div>
        </div>
      </div>
    );
  }

  if (session) return null;

  const verifyEmailNormalized = sessionEmailInput.trim().toLowerCase();

  if (isNarrowSession && isVerifyStage) {
    return (
      <div className="sessionGate">
        <div className="sessionShell sessionShell--verifyMobile">
          <SessionVerifyStep
            email={sessionEmailInput.trim()}
            code={sessionCodeInput}
            setCode={setSessionCodeInput}
            sessionConsentChecked={sessionConsentChecked}
            setSessionConsentChecked={setSessionConsentChecked}
            onSubmit={() => {
              if (!verifyEmailNormalized) {
                setSessionStage('email');
                return;
              }
              if (!sessionConsentChecked) {
                setSessionErrorMessage(t('auth.acceptTerms'));
                return;
              }
              verifySessionCode(verifyEmailNormalized, sessionCodeInput.replace(/\D/g, '').slice(0, 6));
            }}
            onBack={() => {
              setSessionStage('email');
              setSessionCodeInput('');
            }}
            onResend={() => requestSessionCode(verifyEmailNormalized)}
            isSubmitting={isSessionSubmitting}
            isRequestingCode={isSessionRequestingCode}
            resendSeconds={sessionResendSeconds}
            errorMessage={sessionErrorMessage}
            hintCode={sessionHintCode}
          />
          <p className="sessionHint sessionHint--verifyMobileFoot">{t('auth.noPasswordsNeeded')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="sessionGate">
      <div className="sessionShell">
        <section className="sessionShowcase" aria-label={t('auth.signInOrCreate')}>
          <div className="sessionShowcaseGlow sessionShowcaseGlowTop" />
          <div className="sessionShowcaseGlow sessionShowcaseGlowBottom" />
          <div>
            <div className="sessionShowcaseEyebrow">{t('app.tagline')}</div>
            <h1 className="sessionShowcaseTitle">{t('auth.heroTitle')}</h1>
            <p className="sessionShowcaseBody">{t('auth.heroBody')}</p>
          </div>
          <div className="sessionShowcaseHighlights" role="list" aria-label={t('auth.heroHighlightsLabel')}>
            <div className="sessionHighlightCard" role="listitem">
              <span className="sessionHighlightKicker">{t('auth.heroHighlightFastKicker')}</span>
              <strong>{t('auth.heroHighlightFastTitle')}</strong>
              <p>{t('auth.heroHighlightFastBody')}</p>
            </div>
            <div className="sessionHighlightCard" role="listitem">
              <span className="sessionHighlightKicker">{t('auth.heroHighlightPrivateKicker')}</span>
              <strong>{t('auth.heroHighlightPrivateTitle')}</strong>
              <p>{t('auth.heroHighlightPrivateBody')}</p>
            </div>
            <div className="sessionHighlightCard" role="listitem">
              <span className="sessionHighlightKicker">{t('auth.heroHighlightPersonalKicker')}</span>
              <strong>{t('auth.heroHighlightPersonalTitle')}</strong>
              <p>{t('auth.heroHighlightPersonalBody')}</p>
            </div>
          </div>
        </section>

        <div className="sessionCard sessionCardPrimary sessionCard--liquid">
          <div className="brandWordmark brandWordmark--centered">
            <img src="/noox-wordmark-auth.png" alt="NOOX" className="brandLogo brandLogo--session" width={200} height={44} />
          </div>
          <div className="sessionCardIntro">
            {isVerifyStage ? (
              <>
                <div className="sessionEyebrow">{t('auth.verificationRequired')}</div>
                <h2 className="sessionHeading">{t('auth.verifyHeading')}</h2>
                <p className="sessionText">{t('auth.verifySubcopy')}</p>
              </>
            ) : (
              <>
                <div className="sessionEyebrow">{t('auth.signInOrCreate')}</div>
                <h2 className="sessionHeading">{t('auth.welcomeHeading')}</h2>
                {t('auth.welcomeSubcopy').trim() ? (
                  <p className="sessionText">{t('auth.welcomeSubcopy')}</p>
                ) : null}
              </>
            )}
          </div>

          <label className="sessionConsent">
            <input
              type="checkbox"
              checked={sessionConsentChecked}
              onChange={(e) => setSessionConsentChecked(e.target.checked)}
            />
            <span>
              {t('auth.acceptTerms')} <a href="/terms" target="_blank" rel="noreferrer">{t('auth.terms')}</a> {t('auth.and')} <a href="/privacy" target="_blank" rel="noreferrer">{t('auth.privacyPolicy')}</a>.
            </span>
          </label>

          {!isVerifyStage && signInWithSocial && hasSocialProviders && (
            <>
              <SocialSignInButtons
                onGoogleToken={handleGoogleToken}
                onAppleToken={handleAppleToken}
                disabled={isSessionSubmitting}
                consentChecked={sessionConsentChecked}
                onConsentRequired={() => setSessionErrorMessage(t('auth.acceptTerms'))}
              />
              <div className="sessionDivider">{t('auth.orContinueWithEmail')}</div>
            </>
          )}

          {sessionStage === 'email' ? (
            <form
              className="sessionForm"
              onSubmit={(e) => {
                e.preventDefault();
                const normalized = sessionEmailInput.trim().toLowerCase();
                if (!normalized) {
                  setSessionErrorMessage(t('errors.invalidEmail'));
                  return;
                }
                if (!sessionConsentChecked) {
                  setSessionErrorMessage(t('auth.acceptTerms'));
                  return;
                }
                requestSessionCode(normalized);
              }}
            >
              <label className="sessionFieldLabel sessionFieldLabel--sentence" htmlFor="session-email">{t('auth.emailLabel')}</label>
              <input
                id="session-email"
                name="email"
                type="email"
                autoComplete="email"
                placeholder={t('auth.enterEmail')}
                value={sessionEmailInput}
                onChange={(e) => setSessionEmailInput(e.target.value)}
                required
              />
              <button type="submit" disabled={!sessionEmailInput.trim() || isSessionRequestingCode || !sessionConsentChecked}>
                {isSessionRequestingCode ? t('common.loading') : t('auth.sendVerificationCode')}
              </button>
            </form>
          ) : (
            <form
              className="sessionForm"
              onSubmit={(e) => {
                e.preventDefault();
                const normalized = sessionEmailInput.trim().toLowerCase();
                if (!normalized) {
                  setSessionStage('email');
                  return;
                }
                if (!sessionConsentChecked) {
                  setSessionErrorMessage(t('auth.acceptTerms'));
                  return;
                }
                verifySessionCode(normalized, sessionCodeInput.replace(/\D/g, '').slice(0, 6));
              }}
            >
              <div className="sessionVerificationBadge">{t('auth.emailSentBadge', { email: sessionEmailInput })}</div>
              <div className="sessionInstructions">
                {t('auth.enterCode', { email: sessionEmailInput })}
              </div>
              <label className="sessionFieldLabel" htmlFor="session-code">{t('auth.codeLabel')}</label>
              <input
                id="session-code"
                name="verificationCode"
                type="text"
                autoComplete="one-time-code"
                inputMode="numeric"
                placeholder="••••••"
                value={sessionCodeInput}
                onChange={(e) => setSessionCodeInput(e.target.value)}
                required
              />
              <button type="submit" disabled={!sessionCodeInput.trim() || isSessionSubmitting || !sessionConsentChecked}>
                {isSessionSubmitting ? t('common.loading') : t('auth.verifyAndEnter')}
              </button>
              <div className="sessionActions">
                <button
                  type="button"
                  onClick={() => requestSessionCode(sessionEmailInput.trim().toLowerCase())}
                  disabled={isSessionRequestingCode || sessionResendSeconds > 0}
                  title={sessionResendSeconds > 0 ? t('auth.resendIn', { seconds: sessionResendSeconds }) : undefined}
                >
                  {sessionResendSeconds > 0 ? t('auth.resendIn', { seconds: sessionResendSeconds }) : t('auth.resendCode')}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setSessionStage('email');
                    setSessionCodeInput('');
                  }}
                >
                  {t('auth.useDifferentEmail')}
                </button>
              </div>
              {sessionHintCode && <div className="sessionHint">Dev shortcut: code {sessionHintCode}</div>}
            </form>
          )}

          {sessionErrorMessage && <div className="sessionError">{sessionErrorMessage}</div>}
          <div className="sessionHint">{t('auth.noPasswordsNeeded')}</div>
        </div>
      </div>
    </div>
  );
};

export default SessionGate;
